import type {
  ComponentVisibility,
  FormComponent,
  FormDSL,
} from '@commenda/types';

import { walkComponents } from './dsl-traversal';
import type {
  EntryCompletenessStatus,
  MissingEntryField,
  NamedEntryCompletenessStatus,
} from './entry-completeness.types';
import { isComponentReadOnly } from './form-state';
import { LinkedFormDslHelper } from './linked-form-dsl-helper';
import { fieldValidationError } from './validation';

/**
 * Whether an entry picked from a list (an existing person or entity) already answers
 * everything the form linked to the picker requires — the same check its drawer runs on
 * save, run without opening it.
 */
export class EntryCompletenessHelper {
  static readonly MAX_NAMED_FIELDS = 3;

  private static readonly ENTRY_META_KEY = '__meta';

  /**
   * Components whose answers are not a single value on the entry: a summary shows other
   * answers, and nested or repeatable entries are added one by one in the drawer, which
   * enforces their own counts.
   */
  private static readonly SKIPPED_COMPONENTS = new Set([
    'FormSummary',
    'NestedForm',
  ]);

  static findMissingFields(
    dsl: Readonly<FormDSL>,
    values: Readonly<Record<string, unknown>>
  ): MissingEntryField[] {
    const componentMap: Record<string, FormComponent> = {};
    walkComponents(dsl, (component) => {
      if (component.fieldId) componentMap[component.fieldId] = component;
    });
    const isVisible = (visibility?: ComponentVisibility): boolean =>
      LinkedFormDslHelper.isVisible(visibility, values, componentMap);

    const missing: MissingEntryField[] = [];
    walkComponents(dsl, (component, { step, module }) => {
      if (!isVisible(step.visibility) || !isVisible(module.visibility)) return;
      if (module.repeatable?.enabled) return;
      if (!EntryCompletenessHelper.isAnswerable(component)) return;
      if (!isVisible(component.visibility)) return;

      const error = fieldValidationError(
        values[component.fieldId as string],
        component.validation?.rules,
        { isFieldVisible: isVisible }
      );
      if (error) {
        missing.push({
          fieldId: component.fieldId as string,
          label: component.label || (component.fieldId as string),
        });
      }
    });
    return missing;
  }

  /** "SSN, Date of birth, Nationality and 2 more" — the first few labels, then a count. */
  static formatMissingFieldsSummary(
    fields: readonly MissingEntryField[]
  ): string {
    const named = fields
      .slice(0, EntryCompletenessHelper.MAX_NAMED_FIELDS)
      .map((field) => field.label);
    const rest = fields.length - named.length;
    if (rest > 0) return `${named.join(', ')} and ${rest} more`;
    if (named.length <= 1) return named.join('');
    return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
  }

  /**
   * The error that holds the step, or undefined when every entry can be accepted. Missing
   * details come first, since only the user can fix them; then failed checks, then pending ones.
   */
  static getBlockingError(
    entries: readonly NamedEntryCompletenessStatus[]
  ): string | undefined {
    const namesOf = (kind: EntryCompletenessStatus['kind']): string[] =>
      entries.filter((e) => e.status.kind === kind).map((e) => e.name);
    const incomplete = namesOf('incomplete');
    if (incomplete.length > 0) {
      return `Add the missing details for ${EntryCompletenessHelper.formatEntryNames(incomplete)} to continue.`;
    }
    const unchecked = namesOf('unchecked');
    if (unchecked.length > 0) {
      return `Couldn't check the details for ${EntryCompletenessHelper.formatEntryNames(unchecked)}. Try again.`;
    }
    const checking = namesOf('checking');
    if (checking.length > 0) {
      return `Checking the details for ${EntryCompletenessHelper.formatEntryNames(checking)}…`;
    }
    return undefined;
  }

  /** The answers a picker stored for an entry, without its `__meta` bookkeeping. */
  static getEntryAnswers(
    entry: Readonly<Record<string, unknown>> | undefined
  ): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(entry ?? {}).filter(
        ([key]) => key !== EntryCompletenessHelper.ENTRY_META_KEY
      )
    );
  }

  /** "Jane Doe", "Jane Doe and John Roe", or "Jane Doe and 2 others". */
  private static formatEntryNames(names: readonly string[]): string {
    const [first, ...others] = names;
    if (others.length === 0) return first;
    if (others.length === 1) return `${first} and ${others[0]}`;
    return `${first} and ${others.length} others`;
  }

  /** A field someone can fill in the drawer: it has an id and is not read-only. */
  private static isAnswerable(component: FormComponent): boolean {
    if (!component.fieldId) return false;
    if (EntryCompletenessHelper.SKIPPED_COMPONENTS.has(component.component)) {
      return false;
    }
    return !isComponentReadOnly(component);
  }
}
